import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import { BodyText, Stack } from '../../ui';
import { getAllTimeBlocks, isPowerBooked } from '../../../modules/ProAppointmentState';

/**
 * Creates a series of time duration pills for the appointment.
 * For a standard appointment, it simply shows the full appointment duration in minutes.
 * For a power booked appointment, it creates a pill for each segment of the appointment.
 *
 * @example
 *   <ApptDurationPills appointment={appt} />
 */
const propTypes = {
  /** The appointment object. */
  appointment: PropTypes.object.isRequired,
};

export default function AppointmentDurationPills({ appointment }) {
  let timeblocks = getAllTimeBlocks(appointment);

  if (!isPowerBooked(appointment)) {
    timeblocks = [appointment];
  }

  /**
   * Return a friendly duration string for a timeblock
   *
   * @param {Object} timeblock
   * @return {String}
   */
  const friendlyDuration = timeblock => {
    const start = moment(timeblock.local_start);
    const end = moment(timeblock.local_end);
    const duration = moment.duration(end.diff(start));
    const out = [];
    const hours = duration.hours();
    const minutes = duration.minutes();
    if (hours) {
      const hoursLabel = hours > 1 ? 'hrs' : 'hr';
      out.push(`${hours} ${hoursLabel}`);
    }
    if (minutes) {
      const minutesLabel = minutes > 1 ? 'mins' : 'min';
      out.push(`${minutes} ${minutesLabel}`);
    }

    return out.join(' ');
  };

  /**
   * Return a unique key for this timeblock
   * @param {Object} timeblock
   * @return {String}
   */
  const getKey = timeblock => {
    if (timeblock.powerBookingId) {
      return `${timeblock.powerBookingId}:${timeblock.id}:${timeblock.powerBookingDesc}`;
    }
    return timeblock.id;
  };

  return (
    <Stack>
      {timeblocks
        .filter(timeblock => timeblock.local_start !== timeblock.local_end)
        .map(timeblock => (
          <BodyText
            key={getKey(timeblock)}
            className="appt-duration-pill"
            testID={`appt-duration-pill-${timeblock.powerBookingDesc || timeblock.service_name}`}
          >
            <BodyText weight="bold">&#8226;</BodyText>{' '}
            <time dateTime={timeblock.local_start}>
              {timeblock.powerBookingDesc
                ? `${timeblock.powerBookingDesc}: ${friendlyDuration(timeblock)}`
                : friendlyDuration(timeblock)}
            </time>
          </BodyText>
        ))}
    </Stack>
  );
}

AppointmentDurationPills.propTypes = propTypes;
